import { provideTenantAwareHttpClient } from '@web-libs/core/http/infrastructure/server-side/http-client.provider';
import { Meta } from '@web-libs/core/meta/domain/meta.types';
import { getTenantTag } from '@web-libs/features/tenant/api/tenant.tags';
import { Theme } from '../domain/theme.types';

const missingThemeConfiguratioMessage =
  'Missing theme configuration in .env file. Check following variables: DEFAULT_PRIMARY_COLOR, DEFAULT_ON_PRIMARY_COLOR, DEFAULT_WARN_COLOR, DEFAULT_ON_WARN_COLOR';

const allEnvVariablesAreSet = [
  'DEFAULT_PRIMARY_COLOR',
  'DEFAULT_ON_PRIMARY_COLOR',
  'DEFAULT_WARN_COLOR',
  'DEFAULT_ON_WARN_COLOR',
].every((key) => {
    return !!process.env[key]
});
if (!allEnvVariablesAreSet) {
    const isClient = typeof window !== 'undefined';
    if(!isClient){
        throw new Error(missingThemeConfiguratioMessage);
    }
}

interface ThemeService {
  getCssVariables: () => Promise<{ [key: string]: string }>;
  getNewCssVariables: () => Promise<{ [key: string]: string }>;
  getTheme: () => Promise<Theme>;
}

export const ThemeColorMap = {
  primary: '--primary-color',
  onPrimary: '--on-primary-color',
  warn: '--warn-color',
  onWarn: '--on-warn-color',
};

export const createThemeService = (
  getTheme: () => Promise<Theme>
): ThemeService => {
  let theme: Theme | null = null;
  const _getTheme = async () => {
    if (theme === null) {
      theme = await getTheme();
    }
    return theme;
  };

  return {
    // this initializes the libs/shared/ui/theming css variables
    getNewCssVariables: async () => {
      const theme = await _getTheme();
      return {
        '--primary': theme.primary,
        '--on-primary': theme.onPrimary,
        '--warn': theme.warn,
        '--on-warn': theme.onWarn,
      };
    },
    getCssVariables: async () => {
      const theme = await _getTheme();
      return {
        [ThemeColorMap.primary]: theme.primary,
        [ThemeColorMap.onPrimary]: theme.onPrimary,
        [ThemeColorMap.warn]: theme.warn,
        [ThemeColorMap.onWarn]: theme.onWarn,
      };
    },
    getTheme: async () => {
      const theme = await _getTheme();
      return {
        primary: theme.primary,
        onPrimary: theme.onPrimary,
        warn: theme.warn,
        onWarn: theme.onWarn,
      };
    },
  };
};

type ThemeDTO = Meta & {
  id: string;
  tenantId: string;
  color: {
    primary: string;
    onPrimary: string;
    warn: string;
    onWarn: string;
  };
};

const addHashToColor = (color: string) => {
    return color.startsWith('#') ? color : `#${color}`
}

export const provideThemeService = (tenantId: string, lang?: string) => {
  const httpClient = provideTenantAwareHttpClient(tenantId, lang);

  const getTheme = async () => {
    const theme: Theme | null = await httpClient
      .get<ThemeDTO>('/public/tenants/current/theme', {
        tags: [getTenantTag(tenantId)],
      })
      .then((response) => {
        if(!response || !response.color){
            return null
        }
        return response.color
      })
      .catch((error) => {
        console.error('Error fetching theme', error);
        return null;
      });
    
    return {
      primary: addHashToColor(theme?.primary ?? process.env.DEFAULT_PRIMARY_COLOR),
      onPrimary: addHashToColor(theme?.onPrimary ?? process.env.DEFAULT_ON_PRIMARY_COLOR),
      warn: addHashToColor(theme?.warn ?? process.env.DEFAULT_WARN_COLOR),
      onWarn: addHashToColor(theme?.onWarn ?? process.env.DEFAULT_ON_WARN_COLOR),
    };
  };
  return createThemeService(getTheme);
};
